import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

const PENDING_ALERT_DRAFT_KEY = 'pendingAlertDraft';

const GateContext = createContext(null);

export const useGate = () => {
    const ctx = useContext(GateContext);
    if (!ctx) {
        throw new Error('useGate must be used within GateProvider');
    }
    return ctx;
};

/**
 * Auth gate + “Create Alert” continuation after login.
 */
export const GateProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [authMessage, setAuthMessage] = useState('');

    const openAuthGate = useCallback((message) => {
        setAuthMessage(message || 'Sign up to track wallets and create alerts.');
        setAuthModalOpen(true);
    }, []);

    const closeAuthGate = useCallback(() => {
        setAuthModalOpen(false);
    }, []);

    const requestCreateAlert = useCallback((draft = {}) => {
        try {
            sessionStorage.setItem(PENDING_ALERT_DRAFT_KEY, JSON.stringify({ ...draft, ts: Date.now() }));
        } catch {
            /* ignore */
        }
        if (isAuthenticated) {
            navigate('/dashboard/alerts', { state: { openCreateAlert: true, alertDraft: draft } });
            return;
        }
        openAuthGate('Sign up to track wallets and create alerts.');
    }, [isAuthenticated, navigate, openAuthGate]);

    const value = useMemo(
        () => ({
            authModalOpen,
            authMessage,
            openAuthGate,
            closeAuthGate,
            requestCreateAlert,
        }),
        [authModalOpen, authMessage, openAuthGate, closeAuthGate, requestCreateAlert],
    );

    return <GateContext.Provider value={value}>{children}</GateContext.Provider>;
};

export { PENDING_ALERT_DRAFT_KEY };
